import DOMPurify from 'dompurify';

import type { Columns } from '@/components/table/types';
import type { EntityType } from '@/sections/insights/queryBuilder/components/QueryResults/utils/extractQueryResults';
import type { UsernameObject } from '@/sections/insights/queryBuilder/state/QueryUIContext';
import { pluralize } from '@/utils/pluralize.util';

import { adObjectEntity } from './adobjects';
import { adAIACAEntity } from './adobjects/adaiaca';
import { adCertTemplateEntity } from './adobjects/adcerttemplate';
import { adComputerEntity } from './adobjects/adcomputer';
import { adContainerEntity } from './adobjects/adcontainer';
import { adDomainEntity } from './adobjects/addomain';
import { adEnterpriseCAEntity } from './adobjects/adenterpriseca';
import { adGPOEntity } from './adobjects/adgpo';
import { adGroupEntity } from './adobjects/adgroup';
import { adIssuancePolicyEntity } from './adobjects/adissuancepolicy';
import { adLocalGroupEntity } from './adobjects/adlocalgroup';
import { adLocalUserEntity } from './adobjects/adlocaluser';
import { adNTAuthStoreEntity } from './adobjects/adntauthstore';
import { adOUEntity } from './adobjects/adou';
import { adRootCAEntity } from './adobjects/adrootca';
import { adUserEntity } from './adobjects/aduser';
import { assetEntity } from './assets';
import { repositoryEntity } from './assets/repository';
import { awsResourceEntity } from './awsresources';
import { portEntity } from './ports';
import { riskEntity } from './risks';
import { technologyEntity } from './technologies';
import type { QueryEntity } from './types';
import { webApplicationEntity } from './webapplications';
import { webpageEntity } from './webpages';

/**
 * Registry mapping alias entity labels to their QueryEntity instances
 * Used for O(1) lookup of alias entities by label string
 */
const ALIAS_MAP: Record<string, QueryEntity> = {
  ADGroup: adGroupEntity,
  ADUser: adUserEntity,
  ADComputer: adComputerEntity,
  ADGPO: adGPOEntity,
  ADOU: adOUEntity,
  ADContainer: adContainerEntity,
  ADDomain: adDomainEntity,
  ADLocalGroup: adLocalGroupEntity,
  ADLocalUser: adLocalUserEntity,
  ADAIACA: adAIACAEntity,
  ADRootCA: adRootCAEntity,
  ADEnterpriseCA: adEnterpriseCAEntity,
  ADNTAuthStore: adNTAuthStoreEntity,
  ADCertTemplate: adCertTemplateEntity,
  ADIssuancePolicy: adIssuancePolicyEntity,
};

/**
 * Lookup base entity by type string
 * Does NOT include alias entities - use ALIAS_MAP for those
 */
function lookupBaseEntity(type: string): QueryEntity | null {
  switch (type) {
    case 'assets':
      return assetEntity;
    case 'repositories':
      return repositoryEntity;
    case 'webapplications':
      return webApplicationEntity;
    case 'risks':
      return riskEntity;
    case 'adobjects':
      return adObjectEntity;
    case 'awsresources':
      return awsResourceEntity;
    case 'ports':
      return portEntity;
    case 'webpages':
      return webpageEntity;
    case 'technologies':
      return technologyEntity;
    default:
      return null;
  }
}

/**
 * Get QueryEntity for a given entity type
 * Single source of truth for entity-specific logic
 *
 * Handles both lowercase plural (assets, risks) and uppercase singular (Asset, Risk)
 * by normalizing unmatched labels to lowercase plural format.
 *
 * @param entityType - Entity type from GraphQueryResponse
 * @returns QueryEntity with columns and fields, or null if unknown type
 *
 * @example
 * const entity = getQueryEntity('assets');  // Direct match
 * const entity = getQueryEntity('Asset');   // Normalized to 'assets'
 * const columns = entity?.buildColumns();
 * const fieldSuggestions = entity?.fields.map(f => f.key);
 */
export function getQueryEntity(
  entityType: EntityType | string
): QueryEntity | null {
  // 1. Try direct alias lookup (highest priority)
  const aliasMatch = ALIAS_MAP[entityType];
  if (aliasMatch) return aliasMatch;

  // 2. Try base entity lookup
  const baseMatch = lookupBaseEntity(entityType);
  if (baseMatch) return baseMatch;

  // 3. Try normalized lookup (lowercase + pluralize)
  const normalized = pluralize(2, entityType.toLowerCase());
  return ALIAS_MAP[normalized] || lookupBaseEntity(normalized);
}

/**
 * Builds appropriate column definitions based on entity type
 * Backward compatibility adapter for existing TableResults usage
 *
 * @param entityType - Type of entity from GraphQueryResponse
 * @param isGlobal - Whether to prepend customer name column for global mode
 * @param usernames - Map of email to display name for customer lookup
 * @returns Column definitions for TanStack Table
 *
 * @example
 * const columns = buildColumnsForEntity('assets', true, customerDisplayNames);
 */
export function buildColumnsForEntity(
  entityType: EntityType,
  isGlobal?: boolean,
  usernames?: UsernameObject[],
  isPraetorianUser?: boolean
): Columns<unknown> {
  const entity = getQueryEntity(entityType);

  // Build base columns from entity
  let columns: Columns<unknown>;
  if (entity) {
    columns = entity.buildColumns() as Columns<unknown>;
  } else {
    // Fallback for unimplemented entity types
    columns = [
      {
        id: '',
        label: 'Key',
        cell: (item: unknown) => {
          const key = (item as Record<string, unknown>)?.key;
          if (key === undefined || key === null) {
            return '-';
          }
          return typeof key === 'string' ? key : String(key);
        },
      },
    ];
  }

  // Prepend customer name column if in global mode
  if (isGlobal) {
    columns = [
      {
        id: '',
        label: isPraetorianUser === false ? 'Tenant Name' : 'Customer Name',
        fixedWidth: 200,
        minWidth: 200,
        maxWidth: 200,
        cell: (item: unknown) => {
          const itemRecord = item as Record<string, unknown>;
          const email = itemRecord?.username;

          // If no email in row data, show placeholder
          if (email === undefined || email === null) {
            return '-';
          }

          // Look up username object for display name
          const usernameData = usernames?.find(
            username => username.email === email
          );

          // Priority: displayName > email > placeholder
          const displayValue =
            usernameData?.displayName || String(email) || '-';

          // Sanitize for XSS protection
          return DOMPurify.sanitize(displayValue, { ALLOWED_TAGS: [] });
        },
      },
      ...columns,
    ];
  }

  return columns;
}

/**
 * Get all registered query entities
 * Used for populating dynamic entity dropdowns
 *
 * @returns Array of all QueryEntity instances
 *
 * @example
 * const entities = getAllEntities();
 * const primaryLabels = entities.map(e => e.primaryLabel);
 */
export function getAllEntities(): QueryEntity[] {
  return [
    assetEntity,
    repositoryEntity,
    riskEntity,
    adObjectEntity,
    awsResourceEntity,
    portEntity,
    webpageEntity,
    technologyEntity,
  ];
}

/**
 * Get all alias entities for a given base entity
 * Used by NodeLabel to populate secondary dropdown
 *
 * @param entity - Base entity to find aliases for
 * @returns Array of alias QueryEntity instances
 *
 * @example
 * const aliases = getAllAliasesForEntity(adObjectEntity);
 * // Returns [adGroupEntity, adUserEntity, ...]
 */
export function getAllAliasesForEntity(entity: QueryEntity): QueryEntity[] {
  if (!entity?.primaryLabel?.actual) return [];

  return Object.values(ALIAS_MAP).filter(
    alias => alias.alias === entity.primaryLabel.actual
  );
}
